import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { CalcBreakdown } from "@/components/mobilfunk/CalcBreakdown";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Receipt, RefreshCw } from "lucide-react";

export default function MfCommissionCalc() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canRecalc = can(user?.role, "mobilfunk:contracts");
  const [calcStatus, setCalcStatus] = useState("");
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["mf-calc-list", calcStatus], queryFn: async () => (await api.get(`/mobilfunk/contracts?calc_status=${calcStatus}`)).data });

  const columns = [
    { key: "customer", header: "Abschluss", render: (c) => (
      <div className="min-w-0"><div className="truncate font-medium">{c.customer_name}</div>
        <div className="text-xs text-muted-foreground">{c.tariff_name} · {fmtDate(c.closing_date)}</div></div>
    ) },
    { key: "commission", header: "Erwartet", render: (c) => fmtEUR(c.commission_eur) },
    { key: "calc", header: "Status", render: (c) => c.calc_status === "pruefung"
      ? <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20">Zur Prüfung</Badge>
      : <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20">Berechnet</Badge> },
  ];

  return (
    <div data-testid="mf-calc">
      <PageHeader title="Provisionsberechnung" description="Nachvollziehbare erwartete Provisionen je Abschluss – inkl. angewendeter und ausgeschlossener Regeln." />
      <div className="mb-4">
        <Select value={calcStatus || "all"} onValueChange={(v) => setCalcStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="calc-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle</SelectItem><SelectItem value="berechnet">Berechnet</SelectItem><SelectItem value="pruefung">Zur Prüfung</SelectItem></SelectContent>
        </Select>
      </div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="calc-row" onRowClick={(c) => setDetail(c.id)} />
      <CalcDrawer cid={detail} onClose={() => setDetail(null)} canRecalc={canRecalc} onChanged={() => qc.invalidateQueries({ queryKey: ["mf-calc-list"] })} />
    </div>
  );
}

function CalcDrawer({ cid, onClose, canRecalc, onChanged }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mf-calc-detail", cid], queryFn: async () => (await api.get(`/mobilfunk/contracts/${cid}`)).data, enabled: !!cid });
  const c = data?.contract;
  const recalc = async () => {
    try { await api.post(`/mobilfunk/contracts/${cid}/recalculate`); toast.success("Neu berechnet"); qc.invalidateQueries({ queryKey: ["mf-calc-detail", cid] }); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Sheet open={!!cid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="calc-drawer">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Receipt className="h-4 w-4" />{c?.customer_name}</SheetTitle></SheetHeader>
        {c && (
          <div className="mt-4 space-y-4">
            <CalcBreakdown calc={c.calculation} />
            {canRecalc && <Button variant="outline" className="gap-1.5" onClick={recalc} data-testid="calc-recalc"><RefreshCw className="h-4 w-4" />Neu berechnen</Button>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
